// Секцията „Незавършена …" на началния екран на операция — общо за доставка,
// изписване, прехвърляне и инвентаризация: картата на моята чернова с
// „Продължи …" и под нея „Колеги в процес" (чужда заета — „Редактира се").
// Без чернова: при доставка и инвентаризация — празно място с „+ Нова …";
// при изписване и прехвърляне секцията изобщо я няма (`texts.sectionNone`).
// Глупав рендер — черновите идват от `useHomeDrafts`.
//
// `tone="critical"` (инвентаризацията — незавършеното броене държи наличностите
// неверни): картата е червена (рамка + черта 3px вляво, хапче „Незавършена"), а
// редовете на колегите — с червена точка. Редовете на колегите са плътни: ЕДИН
// ред „склад · кой · колко · кога", отрязаното е в подсказката.
import type { ReactNode } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../numbers";
import { PRIMARY_BUTTON_CLASS, SECONDARY_BUTTON_CLASS } from "../list/buttonStyles";
import { ROW_DENSE } from "../list/density";
import { SectionCard, SectionTitle, SignalPill, StatusPill, type PillTone } from "../list/ListBits";
import { SIGNAL_DOT_CLASS } from "../list/signals";
import { TruncatedText } from "../list/TruncatedText";
import type { DraftSummary } from "./draftModel";
import type { DraftTexts } from "./draftTexts";

const DRAFT_STATUS_TONE: Record<DraftSummary["status"], PillTone> = {
  draft: "neutral",
  held: "danger",
  busy: "busy",
};

const initialsOf = (name: string | null) =>
  (name ?? "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

type Props = {
  mine: DraftSummary | null;
  others: DraftSummary[];
  texts: DraftTexts;
  // хапчето до етикета (фактура/стокова, изписване/брак)
  renderBadge?: (documentType: string | null) => ReactNode;
  onNew: () => void;
  // critical — незавършеното е тревога (инвентаризация), не обикновена чернова
  tone?: "default" | "critical";
};

export const DraftHomeSection = ({ mine, others, texts, renderBadge, onNew, tone = "default" }: Props) => {
  const { t } = useTranslation();
  const critical = tone === "critical";
  const linesOf = (draft: DraftSummary) => t(texts.linesLabel, { count: draft.linesCount });
  if (!mine && !others.length && !texts.sectionNone) return null;
  const statusLabel = (status: DraftSummary["status"]) =>
    ({
      draft: t("inventoryPage.draftStatusDraft"),
      held: t("inventoryPage.draftStatusHeld"),
      busy: t("inventoryPage.draftStatusBusy"),
    })[status];
  const badgeOf = (draft: DraftSummary) => (draft.documentType ? renderBadge?.(draft.documentType) : null);
  // Хапчето на моята: при тревога — „Незавършена" (червено), иначе статусът
  const minePill = (draft: DraftSummary) =>
    critical && draft.status === "draft" ? (
      <SignalPill tone="critical">{t("inventoryPage.draftStatusUnfinished")}</SignalPill>
    ) : (
      <StatusPill tone={DRAFT_STATUS_TONE[draft.status]}>{statusLabel(draft.status)}</StatusPill>
    );
  return (
    <section className="flex flex-col gap-3">
      <SectionTitle title={t(texts.sectionTitle)} hint={t(texts.sectionHint)} />
      {mine ? (
        <div
          data-draft-card
          className={`flex flex-wrap items-center gap-4 rounded-xl border bg-panel px-[18px] py-3.5 ${
            critical ? "border-danger/60 border-l-[3px] border-l-danger" : "border-brand/40"
          }`}
        >
          <div className="flex min-w-[220px] flex-1 flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              {badgeOf(mine)}
              <span className="text-base font-semibold text-ink">{mine.label || t(texts.labelFallback)}</span>
              {minePill(mine)}
            </div>
            <span className="text-[13px] text-ink-2">
              {linesOf(mine)} ·{" "}
              {mine.total != null && (
                <>
                  <span className="font-sheet font-semibold text-ink">{formatMoney(mine.total)}</span> ·{" "}
                </>
              )}
              {t("inventoryPage.homeDraftUpdated", { when: dayjs(mine.updatedAt).format("DD.MM HH:mm") })}
            </span>
            {mine.status === "held" && mine.problemText && (
              <span className="self-start rounded-[7px] bg-danger-tint px-2 py-1.5 text-[12.5px] text-ink">
                ⚑ {mine.problemText}
              </span>
            )}
          </div>
          <button type="button" onClick={mine.onOpen} className={PRIMARY_BUTTON_CLASS}>
            {t(texts.continueLabel)}
          </button>
        </div>
      ) : texts.sectionNone ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-shade-4 px-[18px] py-3 text-muted">
          <span>{t(texts.sectionNone)}</span>
          <button type="button" onClick={onNew} className={SECONDARY_BUTTON_CLASS}>
            {t(texts.newLabel)}
          </button>
        </div>
      ) : null}
      {others.length > 0 && (
        <div className="flex flex-col gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted">
            {t("inventoryPage.homeColleagues")}
          </span>
          <SectionCard>
            {others.map((draft, index) => {
              const label = draft.label || t(texts.labelFallback);
              const meta = [
                draft.ownerName ?? "—",
                linesOf(draft),
                dayjs(draft.updatedAt).format("DD.MM HH:mm"),
              ].join(" · ");
              return (
                <button
                  key={draft.key}
                  type="button"
                  onClick={draft.onOpen}
                  className={`flex w-full cursor-pointer items-center gap-2.5 border-none bg-transparent px-3.5 text-left text-ink outline-none hover:bg-fill focus-visible:bg-fill ${ROW_DENSE} ${
                    index ? "border-t border-solid border-shade-1" : ""
                  }`}
                >
                  {critical && (
                    <span aria-hidden className={`h-[7px] w-[7px] shrink-0 rounded-full ${SIGNAL_DOT_CLASS.critical}`} />
                  )}
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-busy-tint text-[10.5px] font-semibold text-busy">
                    {initialsOf(draft.ownerName)}
                  </span>
                  {badgeOf(draft)}
                  <TruncatedText text={`${label} · ${meta}`} className="flex-1">
                    <span className="font-medium">{label}</span>
                    <span className="text-[12.5px] text-body"> · {meta}</span>
                  </TruncatedText>
                  {draft.total != null && (
                    <span className="shrink-0 font-sheet font-semibold">{formatMoney(draft.total)}</span>
                  )}
                  <span className="shrink-0">
                    <StatusPill tone={DRAFT_STATUS_TONE[draft.status]}>{statusLabel(draft.status)}</StatusPill>
                  </span>
                </button>
              );
            })}
          </SectionCard>
        </div>
      )}
    </section>
  );
};

export default DraftHomeSection;
